// src/pages/services/ServiceDetail.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Avatar,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Stack,
  Tooltip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Euro as EuroIcon,
  Groups as GroupsIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ConfirmationNumber as TicketIcon,
  ChildCare as ChildIcon
} from '@mui/icons-material';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`service-tabpanel-${index}`}
      aria-labelledby={`service-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripción: '',
    fechaInicio: new Date(),
    fechaFin: new Date(),
    horario: '',
    lugar: '',
    precio: '',
    plazas: '',
    responsable: '',
    cursos: [],
    active: true,
    inscritos: 0
  });

  const [cursoInput, setCursoInput] = useState('');
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchService = async () => {
      try {
        const docRef = doc(db, 'services', serviceId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();

          // Conversión robusta de timestamp → Date
          // Cubre: Timestamp SDK, objeto plano {seconds,nanoseconds}, Date, null/undefined
          const toDate = (val) => {
            if (!val) return null;
            if (typeof val.toDate === 'function') return val.toDate();          // Timestamp SDK
            if (val.seconds != null) return new Date(val.seconds * 1000);       // objeto plano
            if (val instanceof Date) return val;
            return null;
          };

          setFormData({
            ...data,
            fechaInicio: toDate(data.fechaInicio) ?? new Date(),
            fechaFin:    toDate(data.fechaFin)    ?? new Date(),
            precio: data.precio != null ? data.precio.toString() : '',
            plazas: data.plazas != null ? data.plazas.toString() : ''
          });
          
          // Fetch enrollments for this service
          const enrollmentsQuery = query(
            collection(db, 'serviceEnrollments'),
            where('serviceId', '==', serviceId)
          );
          const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
          const enrollmentsList = [];
          
          for (const docSnapshot of enrollmentsSnapshot.docs) {
            const enrollment = docSnapshot.data();
            const userId = enrollment.userId;
            
            try {
              // Get user info
              const userDoc = await getDoc(doc(db, 'users', userId));
              let userData = { email: 'Usuario desconocido' };
              if (userDoc.exists()) {
                userData = userDoc.data();
              }
              
              // Get children info for each enrollment
              const childrenInfo = [];
              if (enrollment.childrenIds && enrollment.childrenIds.length > 0) {
                for (const childId of enrollment.childrenIds) {
                  const childDoc = await getDoc(doc(db, 'children', childId));
                  if (childDoc.exists()) {
                    childrenInfo.push({
                      id: childId,
                      ...childDoc.data()
                    });
                  } else {
                    childrenInfo.push({
                      id: childId,
                      nombre: 'Hijo no encontrado'
                    });
                  }
                }
              }
              
              enrollmentsList.push({
                id: docSnapshot.id,
                ...enrollment,
                user: userData,
                children: childrenInfo
              });
            } catch (error) {
              console.error(`Error fetching enrollment details:`, error);
            }
          }
          
          setEnrollments(enrollmentsList);
        } else {
          setError('No se encontró el servicio');
          navigate('/admin/services');
        }
      } catch (error) {
        console.error('Error fetching service:', error);
        setError('Error al cargar el servicio');
      } finally {
        setFetching(false);
      }
    };

    fetchService();
  }, [serviceId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle numeric values
    if (name === 'precio' || name === 'plazas') {
      // Allow only numeric values
      const regex = name === 'precio' ? /^$|^[0-9]+(\.[0-9]{0,2})?$/ : /^$|^[0-9]+$/;
      if (regex.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      // Handle other fields
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleDateChange = (date, field) => {
    setFormData({
      ...formData,
      [field]: date
    });
    
    // Clear error when field is edited
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: ''
      });
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleAddCurso = () => {
    if (!cursoInput.trim()) {
      setFormErrors({
        ...formErrors,
        cursos: 'El curso no puede estar vacío'
      });
      return;
    }

    if (!(formData.cursos || []).includes(cursoInput)) {
      setFormData({
        ...formData,
        cursos: [...(formData.cursos || []), cursoInput]
      });
    }
    setCursoInput('');
    
    // Clear error
    if (formErrors.cursos) {
      setFormErrors({
        ...formErrors,
        cursos: ''
      });
    }
  };

  const handleRemoveCurso = (curso) => {
    setFormData({
      ...formData,
      cursos: (formData.cursos || []).filter(c => c !== curso)
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nombre) {
      errors.nombre = 'El nombre es obligatorio';
    }
    
    if (!formData.fechaInicio) {
      errors.fechaInicio = 'La fecha de inicio es obligatoria';
    }
    
    if (!formData.fechaFin) {
      errors.fechaFin = 'La fecha de fin es obligatoria';
    }
    
    if (formData.fechaInicio && formData.fechaFin && formData.fechaInicio > formData.fechaFin) {
      errors.fechaFin = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Format data for Firestore
      const serviceData = {
        ...formData,
        precio: formData.precio ? parseFloat(formData.precio) : 0,
        plazas: formData.plazas ? parseInt(formData.plazas, 10) : null,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, 'services', serviceId), serviceData);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error updating service:', error);
      setError('Error al actualizar el servicio. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteService = async () => {
    try {
      await deleteDoc(doc(db, 'services', serviceId));
      navigate('/admin/services');
    } catch (error) {
      console.error('Error deleting service:', error);
      setError('Error al eliminar el servicio');
      setDeleteDialogOpen(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    try {
      const date = typeof timestamp.toDate === 'function'
        ? timestamp.toDate()
        : timestamp.seconds != null
          ? new Date(timestamp.seconds * 1000)
          : new Date(timestamp);
      return date.toLocaleDateString('es-ES');
    } catch {
      return 'Fecha no disponible';
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  const inscritos   = formData.inscritos || 0;
  const plazasNum   = Number(formData.plazas) || 0;
  const pctCapacity = plazasNum > 0 ? Math.min((inscritos / plazasNum) * 100, 100) : 0;
  const capacityColor = pctCapacity >= 100 ? 'error' : pctCapacity >= 75 ? 'warning' : 'success';

  return (
    <>
      {/* ── Cabecera ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Volver a servicios">
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/services')}>
            Volver
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700} noWrap>
            {formData.nombre || 'Detalle de Servicio'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip
            icon={formData.active ? <CheckCircleIcon /> : <CancelIcon />}
            label={formData.active ? 'Activo' : 'Inactivo'}
            color={formData.active ? 'success' : 'default'}
            size="small"
          />
          {plazasNum > 0 && (
            <Chip
              icon={<TicketIcon />}
              label={`${inscritos} / ${plazasNum} plazas`}
              color={capacityColor}
              variant="outlined"
              size="small"
            />
          )}
          {enrollments.length > 0 && (
            <Chip
              icon={<GroupsIcon />}
              label={`${enrollments.length} inscritos`}
              color="primary"
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* ── Tabs ── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Información del servicio" />
          <Tab label={`Inscripciones (${enrollments.length})`} />
        </Tabs>
      </Paper>

      {/* ══════ TAB 0: FORMULARIO ══════ */}
      <TabPanel value={tabValue} index={0}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>

            {/* ── 1. Información ── */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', borderLeft: '4px solid #1976d2' }}>
              <Box sx={{ px: 3, py: 2, bgcolor: '#1976d208', display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700} color="#1976d2">Información del servicio</Typography>
              </Box>
              <Divider />
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  {/* Nombre grande y prominente */}
                  <TextField
                    fullWidth required
                    label="Nombre del servicio"
                    name="nombre"
                    value={formData.nombre || ''}
                    onChange={handleChange}
                    error={!!formErrors.nombre}
                    helperText={formErrors.nombre || 'Nombre visible para las familias en la app'}
                    inputProps={{ style: { fontSize: '1.1rem', fontWeight: 600 } }}
                  />

                  {/* Descripción con contador de caracteres */}
                  <Box>
                    <TextField
                      fullWidth multiline rows={3}
                      label="Descripción"
                      name="descripción"
                      value={formData.descripción || ''}
                      onChange={handleChange}
                      placeholder="Describe el servicio, objetivos, metodología, a quién va dirigido…"
                      inputProps={{ maxLength: 500 }}
                    />
                    <Typography variant="caption" color="text.disabled" sx={{ float: 'right', mt: 0.5 }}>
                      {(formData.descripción || '').length} / 500
                    </Typography>
                  </Box>

                  {/* Horario, lugar y responsable en tarjetas visuales */}
                  <Grid container spacing={2}>
                    {[
                      { label: 'Horario', name: 'horario', icon: <CalendarIcon />, placeholder: 'Ej: L y X, 16:00–18:00', color: '#1976d2' },
                      { label: 'Lugar', name: 'lugar', icon: <LocationIcon />, placeholder: 'Ej: Gimnasio del colegio', color: '#1976d2' },
                      { label: 'Responsable', name: 'responsable', icon: <PersonIcon />, placeholder: 'Monitor o responsable', color: '#1976d2' },
                    ].map(field => (
                      <Grid item xs={12} sm={4} key={field.name}>
                        <Box sx={{ border: '1px solid #e3f2fd', borderRadius: 2, p: 1.5, bgcolor: '#f8fbff' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, color: field.color }}>
                            {React.cloneElement(field.icon, { sx: { fontSize: 16 } })}
                            <Typography variant="caption" fontWeight={700} color={field.color}>{field.label.toUpperCase()}</Typography>
                          </Box>
                          <TextField
                            fullWidth variant="standard"
                            name={field.name}
                            value={formData[field.name] || ''}
                            onChange={handleChange}
                            placeholder={field.placeholder}
                            InputProps={{ disableUnderline: false }}
                            sx={{ '& input': { fontSize: '0.9rem' } }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Box>
            </Paper>

            {/* ── 2. Período ── */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', borderLeft: '4px solid #7b1fa2' }}>
              <Box sx={{ px: 3, py: 2, bgcolor: '#7b1fa208', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700} color="#7b1fa2">Período del servicio</Typography>
                </Box>
                {/* Duración calculada */}
                {formData.fechaInicio && formData.fechaFin && (() => {
                  const ms = formData.fechaFin - formData.fechaInicio;
                  if (ms <= 0) return null;
                  const days = Math.round(ms / 86400000);
                  const months = Math.round(days / 30);
                  return (
                    <Chip
                      label={months >= 2 ? `${months} meses` : `${days} días`}
                      size="small"
                      sx={{ bgcolor: '#7b1fa218', color: '#7b1fa2', fontWeight: 700 }}
                    />
                  );
                })()}
              </Box>
              <Divider />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2.5} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <DatePicker label="Fecha de inicio" value={formData.fechaInicio}
                      onChange={(d) => handleDateChange(d, 'fechaInicio')}
                      slotProps={{ textField: { fullWidth: true, required: true, error: !!formErrors.fechaInicio, helperText: formErrors.fechaInicio || 'Inicio del servicio' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="text.disabled">→</Typography>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <DatePicker label="Fecha de fin" value={formData.fechaFin}
                      onChange={(d) => handleDateChange(d, 'fechaFin')}
                      slotProps={{ textField: { fullWidth: true, required: true, error: !!formErrors.fechaFin, helperText: formErrors.fechaFin || 'Fin del servicio' } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* ── 3. Precio, capacidad y estado ── */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', borderLeft: '4px solid #2e7d32' }}>
              <Box sx={{ px: 3, py: 2, bgcolor: '#2e7d3208', display: 'flex', alignItems: 'center', gap: 1 }}>
                <EuroIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700} color="#2e7d32">Precio, capacidad y estado</Typography>
              </Box>
              <Divider />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2.5}>

                  {/* Precio */}
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Precio por alumno" name="precio"
                      value={formData.precio || ''} onChange={handleChange}
                      placeholder="0"
                      helperText={!formData.precio || Number(formData.precio) === 0 ? '🎁 Servicio gratuito' : `${Number(formData.precio).toFixed(2)} € por alumno`}
                      InputProps={{ startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" color="action" /></InputAdornment> }}
                    />
                  </Grid>

                  {/* Plazas */}
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Plazas disponibles" name="plazas"
                      value={formData.plazas || ''} onChange={handleChange}
                      placeholder="Sin límite"
                      helperText={plazasNum > 0 ? `${plazasNum - inscritos} plazas libres` : '♾️ Sin límite de plazas'}
                      InputProps={{ startAdornment: <InputAdornment position="start"><GroupsIcon fontSize="small" color="action" /></InputAdornment> }}
                    />
                  </Grid>

                  {/* Barra de capacidad */}
                  {plazasNum > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, bgcolor: `${capacityColor}.50`, borderRadius: 2, border: '1px solid', borderColor: `${capacityColor}.200` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight={700} color={`${capacityColor}.main`}>
                            {inscritos} / {plazasNum} inscritos
                          </Typography>
                          <Typography variant="h6" fontWeight={800} color={`${capacityColor}.main`}>
                            {Math.round(pctCapacity)}%
                          </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={pctCapacity} color={capacityColor}
                          sx={{ height: 12, borderRadius: 6, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">
                          {pctCapacity >= 100 ? '🔴 Completo' : pctCapacity >= 75 ? '🟡 Casi lleno' : '🟢 Plazas disponibles'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Toggle estado */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 2, px: 2.5, borderRadius: 2,
                      bgcolor: formData.active ? '#e8f5e9' : '#fafafa',
                      border: '2px solid', borderColor: formData.active ? '#81c784' : '#e0e0e0',
                      transition: 'all 0.2s'
                    }}>
                      <Stack spacing={0.25}>
                        <Typography variant="body1" fontWeight={700} color={formData.active ? '#2e7d32' : 'text.secondary'}>
                          {formData.active ? '✓ Servicio activo' : '— Servicio inactivo'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.active
                            ? 'Las familias pueden ver e inscribirse en la app'
                            : 'El servicio está oculto y no acepta inscripciones'}
                        </Typography>
                      </Stack>
                      <Switch checked={formData.active || false} onChange={handleSwitchChange} name="active" color="success" />
                    </Box>
                  </Grid>

                </Grid>
              </Box>
            </Paper>

            {/* ── 4. Cursos ── */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', borderLeft: '4px solid #e65100' }}>
              <Box sx={{ px: 3, py: 2, bgcolor: '#e6510008', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon sx={{ color: '#e65100', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700} color="#e65100">Cursos destinatarios</Typography>
                </Box>
                {(formData.cursos || []).length > 0 && (
                  <Chip label={`${formData.cursos.length} cursos`} size="small"
                    sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                )}
              </Box>
              <Divider />
              <Box sx={{ p: 3 }}>
                {/* Accesos rápidos */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                  ACCESOS RÁPIDOS
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2.5 }}>
                  {['P3','P4','P5','1r','2n','3r','4t','5è','6è'].map(c => {
                    const selected = (formData.cursos || []).includes(c);
                    return (
                      <Chip key={c} label={c} size="small" clickable
                        onClick={() => {
                          if (selected) handleRemoveCurso(c);
                          else { setCursoInput(''); setFormData(p => ({ ...p, cursos: [...(p.cursos||[]), c] })); }
                        }}
                        sx={{
                          fontWeight: 700, fontSize: 12,
                          bgcolor: selected ? '#e65100' : '#fff3e0',
                          color: selected ? '#fff' : '#e65100',
                          border: '1px solid #e65100',
                          '&:hover': { bgcolor: selected ? '#bf360c' : '#ffe0b2' }
                        }}
                      />
                    );
                  })}
                </Stack>

                {/* Input manual */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                  AÑADIR MANUALMENTE
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField size="small" fullWidth label="Otro curso" value={cursoInput}
                    onChange={(e) => setCursoInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCurso())}
                    error={!!formErrors.cursos} helperText={formErrors.cursos}
                    placeholder="Escribe y pulsa Enter o +"
                  />
                  <Button variant="contained" onClick={handleAddCurso}
                    sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }} disableElevation>
                    <AddIcon />
                  </Button>
                </Box>

                {/* Chips seleccionados */}
                {(formData.cursos || []).length === 0
                  ? <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="#e65100" fontStyle="italic">
                        Sin restricción — el servicio está abierto a todos los cursos
                      </Typography>
                    </Box>
                  : <Stack direction="row" flexWrap="wrap" gap={1}>
                      {(formData.cursos || []).map((c, i) => (
                        <Chip key={i} label={c} onDelete={() => handleRemoveCurso(c)}
                          sx={{ bgcolor: '#e65100', color: '#fff', fontWeight: 700,
                            '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }
                          }}
                        />
                      ))}
                    </Stack>
                }
              </Box>
            </Paper>

            {/* ── Barra de acciones ── */}
            <Paper sx={{ borderRadius: 2, p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
                  onClick={handleDeleteDialogOpen} disabled={loading}>
                  Eliminar servicio
                </Button>
                <Stack direction="row" spacing={1.5}>
                  <Button variant="outlined" onClick={() => navigate('/admin/services')} disabled={loading}>
                    Cancelar
                  </Button>
                  <Button type="submit" variant="contained" size="large"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    disabled={loading} sx={{ fontWeight: 700, px: 3 }}>
                    {loading ? 'Guardando…' : 'Guardar cambios'}
                  </Button>
                </Stack>
              </Box>
            </Paper>

          </Stack>
        </form>
      </TabPanel>

      {/* ══════ TAB 1: INSCRIPCIONES ══════ */}
      <TabPanel value={tabValue} index={1}>
        {enrollments.length === 0 ? (
          <Paper sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
            <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.50', mx: 'auto', mb: 2 }}>
              <GroupsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary">Sin inscripciones</Typography>
            <Typography variant="body2" color="text.disabled">Aún no hay familias inscritas en este servicio</Typography>
          </Paper>
        ) : (
          <>
            {/* Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              {[
                { label: 'Total inscritos', value: enrollments.length, color: 'primary.main' },
                { label: 'Plazas ocupadas', value: plazasNum > 0 ? `${inscritos}/${plazasNum}` : inscritos, color: `${capacityColor}.main` },
                { label: 'Hijos totales', value: enrollments.reduce((acc, e) => acc + (e.children?.length || 0), 0), color: 'secondary.main' }
              ].map((s, i) => (
                <Paper key={i} sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={800} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Paper>
              ))}
            </Box>

            {/* Tabla */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Familia</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Hijos inscritos</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fecha inscripción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enrollments.map(enrollment => (
                      <TableRow key={enrollment.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.100', color: 'primary.dark' }}>
                              {((enrollment.user.nombre || enrollment.user.email || '?')[0]).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>
                              {enrollment.user.nombre} {enrollment.user.apellidos || ''}
                              {!enrollment.user.nombre && enrollment.user.email}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{enrollment.user.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {(enrollment.children || []).length === 0
                              ? <Typography variant="caption" color="text.disabled">—</Typography>
                              : enrollment.children.map(child => (
                                  <Chip
                                    key={child.id}
                                    icon={<ChildIcon sx={{ fontSize: '13px !important' }} />}
                                    label={`${child.nombre || '?'} ${child.curso ? `· ${child.curso}` : ''}`}
                                    size="small" variant="outlined" color="primary"
                                  />
                                ))
                            }
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(enrollment.enrollmentDate || enrollment.createdAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>¿Eliminar servicio?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar el servicio "{formData.nombre}"? Esta acción no se puede deshacer.
            {enrollments.length > 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Hay {enrollments.length} inscripciones en este servicio. Si eliminas el servicio, estas inscripciones también se verán afectadas.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancelar</Button>
          <Button onClick={handleDeleteService} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">
          Servicio actualizado exitosamente
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ServiceDetail;